// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from "@crm-src/hooks/use-toast"
import { useResellers } from "@crm-src/hooks/useResellers"
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  ListSubheader,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { PageLoader } from "@/components/ui/page-loader"
import { withProspectsAccess } from "@crm-src/components/page-permission-guard"
import {
  Edit,
  X,
  User,
  Activity as ActivityIcon,
  TrendingUp,
  Contact,
  Folder,
  MoreHorizontal,
  Mail,
  Phone,
  Users,
} from "lucide-react"
import { ProspectFilesPanel } from "@/pages/portal/files/ProspectFilesPanel"

import { leadsApi as leadsApiService, isAiProspect } from "@crm-src/lib/leads-api"
import { prospectListPathForLead } from "../prospects-origin"
import type {
  DetailedLead,
  LeadPriority
} from "@crm-src/types/lead"

// RESELLER PORT. Three different reasons live in this file, and they are not
// interchangeable:
//
// (1) PERMISSION, AND IT IS GRANTED. Convert and the Actions menu (Edit /
//     Delete) are back since spec 2026-09-09 §4.5 — crm-api's
//     `LeadsController` carries `allow_reseller_actor :index, :show, :create,
//     :update, :destroy, :convert` with `reseller_permission "prospects:edit"`
//     on the four writes (`prospects:view` on the two reads). They are GATED
//     on that key here, never unconditional: a member without it sees exactly
//     the read-only page the port first shipped. `prospect-form-modal` and
//     `delete-prospect-modal` came into the tree with them. Convert keeps the
//     source's own post-convert navigation — back to
//     `prospectListPathForLead(prospect)` — which is the right landing here
//     for a reason the source does not have: the row IS a Customer
//     afterwards, so this page's own route no longer resolves it. App.tsx
//     points `/ai-prospects` at `/prospects`, so both origins land on the
//     reseller's one list.
//
// (2) PERMISSION ON A DIFFERENT KEY, and the difference is the ruling. The
//     FILES tab below is this portal's own, not the source's (spec
//     2026-09-09 §3.3 / §4.4). `<ProspectFilesPanel leadId>` mounts the
//     ported `FileManagerPanel` with `api={leadAttachmentsApi}` and
//     `scope="prospect"`, so it asks `/leads/:id/attachments` — crm-api's
//     `LeadAttachmentsController < CustomerAttachmentsController`, mounted
//     under `resources :leads` precisely so that a prospect's files and a
//     customer's can answer to different keys. All five actions (index,
//     download, view, upload, destroy) are granted to a reseller actor under
//     `prospects:view`: the one place in the catalog where a WRITE hangs off
//     a view key, because a file a reseller attaches to a lead is the
//     RESELLER'S own document about the lead rather than the lead's data.
//     Destroy is narrowed once more, from the blob's metadata rather than a
//     permission key — only a blob this reseller's own company uploaded,
//     which crm-api's index reports per row as `deletable`. So a member
//     holding nothing beyond `prospects:view` can still add a file here while
//     every control in (1) stays hidden. That asymmetry IS the ruling; it is
//     not an oversight to "fix" to `prospects:edit`.
//
// (3) NOT PERMISSION — these stay out whatever keys the member holds. Share
//     into the staff chat and the "AI Lead" jump into the command center's
//     Sales section are staff surfaces this portal does not have, and their
//     modules (`ai-lead-brief-modal`, `@xop/dashboard-shared`) are not copied
//     into this tree.
//
//     The source's shared "Notes & Files" TAB is also still not mounted — but
//     every reason this comment has given for that in the past is now dead,
//     so do not reinstate one of them:
//       · NOT "there is nothing to call". `notes-panel.tsx` is in this tree,
//         and though routes.rb nests notes under `customers` only,
//         `/customers/:id/notes` resolves for a lead id anyway: `Lead <
//         Customer` (STI) and `customer_notes_controller#set_customer` scopes
//         through `current_tenant.customers`.
//       · NOT "crm-api refuses a reseller every note write".
//         `customer_notes_controller` grants a reseller actor
//         `create`/`update`/`destroy` under `customers:edit`, and
//         `index`/`show` under `customers:view`.
//       · NOT "attachment upload and destroy carry no reseller grant (R31)".
//         Spec §4.4 lifted exactly that: `customer_attachments_controller` now
//         grants `upload`/`destroy` under `customers:edit`, and prospects got
//         their own controller — which is what (2) mounts.
//     What is actually left is this: on a PROSPECT page, both halves of that
//     shared panel answer to the CUSTOMER keys. Files were given a controller
//     of their own under `leads` so a prospect's files could follow a prospect
//     key; notes were not (there is no `lead_notes_controller`), so mounting
//     `NotesPanel` here would write a prospect's notes through
//     `customers:edit` — a key this page never asks about — with no ruling
//     behind it, and the shared panel has no read-only mode to fall back on.
//     The Customers detail page keeps the whole "Notes & Files" tab, where
//     both halves answer to the keys that page is already gated by.
//
//     The Activity tab's "Schedule Activity" button is still off
//     (`showScheduleButton={false}`), and that one IS a flat refusal:
//     `activities_controller` carries no `allow_reseller_actor` at all, so
//     Layer 3 turns it away before a permission key is ever consulted.
//     The shared `EditableContactInformationPanel` (Add Contact + per-row
//     Edit/Delete menu) is likewise still replaced below by a local,
//     presentational-only render of the same fields — for the notes-shaped
//     reason, not a missing grant: `customer_contacts_controller` DOES grant a
//     reseller `create`/`update`/`destroy`, but under `customers:edit`, on
//     `/customers/:id/contacts`.
//     None of the shared panels themselves were touched — see
//     src/crm/README.md for why the Customers page must keep the writable
//     ones.
// Pinned by ProspectsDetailPage.test.tsx. See src/crm/README.md.
//
// The QUOTES TAB IS GONE (spec 2026-09-09 §3.2). It listed the reseller's own
// `document_type: "quote"` invoices for this lead — a document class the
// portal no longer has a section for. A reseller's own documents for a
// prospect now live in the file manager with that customer or prospect, which
// is the product owner's own ruling ("we allow saving them into the file
// manager for each customer or prospect"). The financial-access wall went with
// it: it existed only to hide a priced tab, and there is no longer one on this
// page.

// Import panel components
import { ActivityLogPanel } from "@crm-src/components/activity-log-panel"
import { ProspectFormModal } from "@crm-src/components/prospect-form-modal"
import { DeleteProspectModal } from "@crm-src/components/delete-prospect-modal"
import { DetailTabs } from "@crm-src/components/detail-tabs"
import { ProspectOverviewTab } from "@crm-src/components/prospect-overview-tab"
import { useTabParam } from "@crm-src/hooks/useTabParam"
import usePermissions from "@crm-src/hooks/usePermissions"

import type { DetailTab } from "@crm-src/components/detail-tabs"
import { BackButton } from "@/components/ui/back-button"




const PROSPECT_TABS: DetailTab[] = [
  // Mirrors the customer detail page: Overview shows info + contacts side
  // by side (no separate Contacts tab).
  { id: "overview", label: "Overview", icon: User },
  { id: "activity", label: "Activity", icon: ActivityIcon },
  // RESELLER PORT: the Files tab is this portal's, not the source's. The
  // source has a "Notes & Files" tab whose NOTES half crm-api still refuses a
  // reseller; the files half became possible with spec 2026-09-09 §4.4, so
  // the tab is files only and the panel is reached through a portal-owned
  // wrapper. See src/crm/README.md.
  { id: "files", label: "Files", icon: Folder },
]
const PROSPECT_TAB_IDS = PROSPECT_TABS.map((tab) => tab.id)

// RESELLER PORT: read-only replacement for
// `@crm-src/components/editable-contact-information-panel`'s
// EditableContactInformationPanel. That shared panel offers "Add Contact"
// and, per contact, an "Open menu" with Edit/Delete Contact — all three
// write through `customer_contacts_controller`, which crm-api does grant a
// reseller actor, but under `customers:edit` on `/customers/:id/contacts`.
// That is the CUSTOMER key, on a page gated by the prospect ones, and no
// ruling has extended it here — see reason (3) in the import block, where
// notes sit for the same reason. Kept local to this file rather than editing the
// shared panel (which the Customers page still needs writable) or adding
// a `readOnly` prop to it (the panel is a verbatim port; see
// src/crm/README.md). Mirrors the shared panel's two display modes
// (single legacy contact vs. a `customer_contacts` list) without the
// controls, the expand/collapse, or the dialogs.
function ReadOnlyContactInformationPanel({ data }: { data: DetailedLead }) {
  const contacts = data.customer_contacts && data.customer_contacts.length > 0 ? data.customer_contacts : []

  if (contacts.length === 0) {
    if (!data.full_name && !data.email) {
      return (
        <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
          <Box sx={{ display: "flex", justifyContent: "center", color: "text.disabled", mb: 1 }}><Users size={32} /></Box>
          <Typography sx={{ fontSize: 14 }}>No contacts added yet</Typography>
        </Box>
      )
    }
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ display: "flex", color: "text.disabled" }}><User size={16} /></Box>
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>{data.full_name || "N/A"}</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ display: "flex", color: "text.disabled" }}><Mail size={16} /></Box>
          <Typography sx={{ fontSize: 14, color: data.email ? "text.secondary" : "text.disabled", ...(data.email ? {} : { fontStyle: "italic" }) }}>
            {data.email || "Email not specified"}
          </Typography>
        </Box>
        {data.phone && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ display: "flex", color: "text.disabled" }}><Phone size={16} /></Box>
            <Typography sx={{ fontSize: 14, color: "text.secondary" }}>{data.phone}</Typography>
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {contacts.map((contact) => (
        <Box key={contact.id} sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{contact.display_name}</Typography>
          {contact.job_title && (
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{contact.job_title}</Typography>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ display: "flex", color: "text.disabled" }}><Mail size={14} /></Box>
            <Typography sx={{ fontSize: 12, color: contact.email ? "text.secondary" : "text.disabled" }}>
              {contact.email || "Email not specified"}
            </Typography>
          </Box>
          {contact.phone && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ display: "flex", color: "text.disabled" }}><Phone size={14} /></Box>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{contact.phone}</Typography>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  )
}

function ProspectDetailsPage() {
  const params = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { isAdmin, hasPermission } = usePermissions()
  // RESELLER PORT: the same gate the list page uses (Task 6). crm-api's
  // reseller_permission "prospects:edit" is the wall; this only decides
  // whether the control is drawn.
  const canEditProspects = hasPermission("prospects", "edit")
  const { resellers, isLoading: resellersLoading } = useResellers()
  const prospectId = params.id as string
  const [activeTab, setActiveTab] = useTabParam(PROSPECT_TAB_IDS, "overview")

  // State management
  const [prospect, setProspect] = useState<DetailedLead | null>(null)
  const [loading, setLoading] = useState(true)

  // Actions menu anchor — replaces the shadcn DropdownMenu during the MUI 5
  // migration.
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null)

  // Modal states. RESELLER PORT: the source's `chatShareOpen` / `aiLeadModalOpen`
  // stay out — see reason (3) in the import block.
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  // Note: the Activities tab on this page is currently waiting for the
  // CRM API to expose a `/leads/:id/activities` endpoint. Previously
  // this file shipped two hard-coded "demo" datasets that rendered into
  // the live UI — staff users saw fake John Smith / Sarah Johnson rows
  // for every prospect. Both datasets have been removed; until the API
  // ships, the tab should render nothing (or an explicit empty state)
  // rather than fictional data.

  // Load prospect details
  const loadProspect = async () => {
    try {
      setLoading(true)
      
      const prospectResponse = await leadsApiService.getById(prospectId)
      const lead = prospectResponse.lead
      
      setProspect(lead)
      
    } catch (error) {
      console.error('Error loading prospect details:', error)
      toast({
        title: "Error",
        description: "Failed to load prospect details",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (prospectId) loadProspect()
  }, [prospectId])

  // Inline edit (Overview tab): assign salesperson / change priority in place
  // without opening the edit modal. Both throw on failure so the shared
  // <InlineEditField> surfaces the error toast; on success we reload.
  // See docs/crm.md → "Inline editing".
  const handleInlineAssignSalespeople = async (userIds: string[]) => {
    await leadsApiService.bulkAssignSalespeople([prospectId], userIds)
    await loadProspect()
    toast({ title: "Saved", description: "Salesperson updated" })
  }

  const handleInlineChangePriority = async (value: LeadPriority) => {
    await leadsApiService.update(prospectId, { priority: value })
    await loadProspect()
    toast({ title: "Saved", description: "Priority updated" })
  }

  const handleInlineChangeIndustry = async (value: string) => {
    await leadsApiService.update(prospectId, { industry: value })
    await loadProspect()
    toast({ title: "Saved", description: "Industry updated" })
  }

  // Reseller assignment reuses the bulk endpoint (bulk-of-one); "none" === Direct.
  const handleInlineAssignReseller = async (resellerId: string) => {
    await leadsApiService.bulkAssignReseller([prospectId], resellerId)
    await loadProspect()
    toast({ title: "Saved", description: "Reseller updated" })
  }

  // Reseller options for the inline-edit select. "none" === Direct — the same
  // sentinel the bulk-assign-reseller modal sends.
  const resellerOptions = [
    { value: "none", label: "Direct" },
    ...resellers.map((r) => ({ value: String(r.id), label: r.name })),
  ]

  // Handle edit prospect
  const handleEditProspect = () => {
    setIsEditDialogOpen(true)
  }

  // Handle prospect update from modal
  const handleProspectUpdated = (updatedProspect: DetailedLead) => {
    setProspect(updatedProspect)
  }

  // Handle delete prospect
  const handleDeleteProspect = () => {
    setIsDeleteDialogOpen(true)
  }

  // Handle navigation after successful deletion. Return to the list this
  // prospect belongs to (AI prospects → /crm/ai-prospects).
  const handleNavigateToList = () => {
    navigate(prospectListPathForLead(prospect))
  }

  // Handle convert to customer
  const handleConvertToCustomer = async () => {
    if (!prospect) return
    
    try {
      const result = await leadsApiService.convert(prospect.id)
      
      toast({
        title: "Success", 
        description: `${prospect.full_name} converted successfully! Created customer contact.`,
      })
      
      // Navigate back to the list this prospect came from.
      navigate(prospectListPathForLead(prospect))

    } catch (error: any) {
      console.error('Error converting to customer:', error)
      
      const errorData = error?.response?.data
      const errorMessage = errorData?.error || 'Failed to convert to customer'
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      })
    }
  }

  if (loading) {
    return <PageLoader />
  }

  if (!prospect) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 6, textAlign: "center" }}>
        <Box sx={{ mx: "auto", display: "flex", height: 64, width: 64, alignItems: "center", justifyContent: "center", borderRadius: "50%", bgcolor: (t) => alpha(t.palette.error.main, 0.1), mb: 2 }}>
          <Box sx={{ display: "flex", color: "error.main" }}><User size={32} /></Box>
        </Box>
        <Typography sx={{ fontSize: "1.125rem", fontWeight: 600, color: "text.primary", mb: 1 }}>
          Prospect not found
        </Typography>
        <Typography sx={{ fontSize: "0.875rem", color: "text.secondary", mb: 3, maxWidth: 384 }}>
          The prospect you're looking for doesn't exist or you don't have permission to view it.
        </Typography>
        <BackButton label="Back to Prospects" onClick={() => navigate(prospectListPathForLead(prospect))} />
      </Box>
    )
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Header: back + record-level actions. RESELLER PORT: the actions are
          gated on prospects:edit; the share menu the source puts between them
          stays out for reason (3) in the import block. */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <BackButton onClick={() => navigate(prospectListPathForLead(prospect))} />
        {canEditProspects && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<TrendingUp size={16} />}
              onClick={handleConvertToCustomer}
            >
              Convert to Customer
            </Button>
            <Button variant="outlined" color="inherit" size="small" startIcon={<MoreHorizontal size={16} />} onClick={(e) => setActionsAnchor(e.currentTarget)}>
              Actions
            </Button>
            <Menu
              anchorEl={actionsAnchor}
              open={Boolean(actionsAnchor)}
              onClose={() => setActionsAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <ListSubheader sx={{ lineHeight: 2, bgcolor: "transparent" }}>Prospect Actions</ListSubheader>
              <MenuItem onClick={() => { setActionsAnchor(null); handleEditProspect(); }}>
                <Box sx={{ mr: 1, display: "flex" }}><Edit size={16} /></Box>
                Edit Prospect
              </MenuItem>
              <Divider />
              <MenuItem
                sx={{ color: "error.main" }}
                onClick={() => { setActionsAnchor(null); handleDeleteProspect(); }}
              >
                <Box sx={{ mr: 1, display: "flex" }}><X size={16} /></Box>
                Delete Prospect
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Box>

      {/* Identity */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Avatar
          src={`https://api.dicebear.com/7.x/initials/svg?seed=${prospect.company_name || prospect.full_name}`}
          sx={{ width: 56, height: 56 }}
        >
          {prospect.company_name
            ? prospect.company_name.split(' ').map(word => word[0]).join('').substring(0, 2).toUpperCase()
            : `${prospect.first_name?.[0] || 'P'}${prospect.last_name?.[0] || 'R'}`}
        </Avatar>
        <Box>
          <Typography component="h2" sx={{ fontSize: "1.5rem", fontWeight: 700, color: "text.primary" }}>{prospect.company_name || 'Unknown Company'}</Typography>
          <Box component="span" sx={{ color: "text.secondary", fontSize: "0.875rem", bgcolor: "action.hover", px: 1, py: 0.5, borderRadius: 1, display: "inline-block", mt: 0.5 }}>{prospect.customer_id || prospect.id}</Box>
        </Box>
      </Box>

      {/* Section tabs. RESELLER PORT: no "AI Lead" action tab — that jumps
          into the command center's Sales section, which this portal has no
          equivalent of. See the import-block comment above. */}
      <DetailTabs
        tabs={PROSPECT_TABS}
        activeId={activeTab}
        onTabChange={setActiveTab}
        ariaLabel="Prospect sections"
      />

      {/* Active tab content */}
      {activeTab === 'overview' && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 3, alignItems: "start" }}>
          <ProspectOverviewTab
            prospect={prospect}
            editable={isAdmin()}
            onAssignSalespeople={handleInlineAssignSalespeople}
            onChangePriority={handleInlineChangePriority}
            onChangeIndustry={handleInlineChangeIndustry}
            onChangeReseller={handleInlineAssignReseller}
            resellerOptions={resellerOptions}
          />
          <Card>
            <CardHeader
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Contact size={20} />
                  <Typography component="span" sx={{ fontWeight: 600 }}>Contacts</Typography>
                </Box>
              }
            />
            <CardContent>
              <ReadOnlyContactInformationPanel data={prospect} />
            </CardContent>
          </Card>
        </Box>
      )}

      {activeTab === 'activity' && (
        <ActivityLogPanel
          customerId={prospectId}
          customerName={prospect.full_name}
          // RESELLER PORT: no "Schedule" button — it creates a task/activity,
          // an endpoint crm-api refuses to a reseller actor. See the
          // import-block comment above.
          showScheduleButton={false}
          autoRefresh={true}
        />
      )}

      {activeTab === 'files' && <ProspectFilesPanel leadId={prospectId} />}

      {/* The modals the restored actions drive. Under the same gate, so a
          reader never carries them at all. */}
      {canEditProspects && (
        <>
          {/* Edit Prospect Modal */}
          <ProspectFormModal
            isOpen={isEditDialogOpen}
            onClose={() => setIsEditDialogOpen(false)}
            prospect={prospect}
            onSuccess={handleProspectUpdated}
          />

          {/* Delete Prospect Modal */}
          <DeleteProspectModal
            isOpen={isDeleteDialogOpen}
            onClose={() => setIsDeleteDialogOpen(false)}
            prospect={prospect}
            onNavigateToList={handleNavigateToList}
          />
        </>
      )}
    </Box>
  )
}

export default withProspectsAccess(ProspectDetailsPage)
